import React from 'react';
import { Building2 } from 'lucide-react';

/**
 * VerifiedPartner badge — shown next to any seller who has
 * account_type === "partner" on their user record. Distinct from the
 * plain "VERIFIED" badge used for individual creators so buyers can
 * tell an enterprise account apart at a glance.
 *
 * size: 'xs' | 'sm' | 'md' — controls padding + icon + label size
 * withLabel: when false, renders icon-only (useful for dense grids)
 */
export function PartnerBadge({ size = 'sm', withLabel = true, className = '' }) {
  const sizeMap = {
    xs: {
      pad: 'px-1.5 py-0.5',
      icon: 'w-3 h-3',
      text: 'text-[9px]',
      gap: 'gap-1',
    },
    sm: {
      pad: 'px-2 py-0.5',
      icon: 'w-3 h-3',
      text: 'text-[10px]',
      gap: 'gap-1',
    },
    md: {
      pad: 'px-3 py-1',
      icon: 'w-4 h-4',
      text: 'text-xs',
      gap: 'gap-1.5',
    },
  };
  const s = sizeMap[size] || sizeMap.sm;

  return (
    <span
      className={`inline-flex items-center ${s.gap} ${s.pad} rounded-full bg-gradient-to-r bg-[#00F0FF]/15 border border-[#00F0FF]/40 backdrop-blur-sm ${className}`}
      data-testid="partner-badge"
      title="Verified Enterprise Partner — account reviewed and approved by theailedger.ai"
    >
      <Building2 className={`${s.icon} text-[#00F0FF] shrink-0`} />
      {withLabel && (
        <span className={`${s.text} font-semibold tracking-wider uppercase text-[#00F0FF]`}>
          Partner
        </span>
      )}
    </span>
  );
}

/**
 * Hook-friendly helper — detects partner status from any seller-shaped
 * object (product.seller, creator, store_owner, etc.). Returns true if
 * account_type==='partner' OR is_partner===true.
 */
export function isPartnerSeller(seller) {
  if (!seller) return false;
  const accountType = (seller.account_type || '').toLowerCase();
  return accountType === 'partner' || seller.is_partner === true;
}

export default PartnerBadge;
